import { useFileFetcher } from '@/services/queryUtils';
import { useDerivedNetworkContext } from '@/context';
import { downloadFile } from '@/utils/dataDownloadUtils';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useReleaseFlag } from '@/hooks';
import type { DateRange, ShowFilter } from '@/types';
import { getDataAvailability } from './useIsBeforeDataAvailability';

/**
 * Custom hook to handle CSV export for age plays data.
 * Calls the server-side export endpoint and triggers a file download.
 * Applies the same audienceSources filter used by AgeModalContent.
 * @returns Object containing handleCSVExport function
 */
const useAgeCsvExport = () => {
  const fetchFile = useFileFetcher();
  const { selectedNetwork } = useDerivedNetworkContext();
  const { audienceSources } = useFlags<{ audienceSources?: string[] }>();
  const sources = audienceSources ?? ['acast', 'phyllo'];
  const flag = useReleaseFlag('audience_page_backfill');

  const handleCSVExport = async (
    dateRange: DateRange | undefined,
    selectedShow: ShowFilter
  ) => {
    if (!dateRange) return;

    const { hideContent, queryFrom } = getDataAvailability('age', dateRange.from, dateRange.to);
    if (hideContent) return;

    const showIdsParam =
      selectedShow && selectedShow !== 'all' ? `&showIds=${selectedShow}` : '';
    const sourcesParam = sources.map((source) => `&sources=${source}`).join('');
    const flagParam = flag ? `&flag=${flag}` : '';
    const path = `/v2/networks/${selectedNetwork.id}/audience/age-plays/export?from=${queryFrom}&to=${dateRange.to}${showIdsParam}${sourcesParam}${flagParam}`;
    const { blob, fileName } = await fetchFile(path);
    downloadFile(blob, fileName);
  };

  return { handleCSVExport };
};

export default useAgeCsvExport;
